// Reine Rechnung der Netzwerk-Seite, Stand 03 „Leitungen und eine Liste
// je Standort" (Mockup docs/mockups/netzwerk-modern.html, Tim 30.09.2026:
// „Empfehlung passt"). Ohne Svelte, damit testbar (lib/netz.test.mjs).
//
// Die Seite beantwortet zwei Fragen: Geht das Internet, und ist im Netz
// jemand weg, der da sein sollte? Normales ist leise: „da" ist ein Wort,
// Handys und Uhren sind offline „unterwegs", ein schlafender Rechner
// „schläft". Gelb heißt: ein Gerät, das zu Hause bleibt, antwortet nicht.

import type { LageTeil } from "../lage";
import type { OpenwrtSnapshot } from "../network-details.svelte";
import type { NetworkTopology } from "../types";
import type { Alert } from "../meldungs-logik";

// "ok" = Normales (StatusBadge: leise, mit „alles, auch ok" grün); "" = kein
// Urteil (schläft, unterwegs, unbekannt) -- bleibt immer leise.
export type NetzTon = "" | "ok" | "warn" | "danger" | "accent";

export interface NetzZustand {
  text: string;
  ton: NetzTon;
}

/** Mbit/s mit deutschem Komma: eine Nachkommastelle, unter 1 zwei. */
export function mbit(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return (n < 1 ? n.toFixed(2) : n.toFixed(1)).replace(".", ",");
}

// ── Internet je Router (seit 25.10.2026) ───────────────────────────
//
// Die Form von /v2-api/wan/summary und dem WS-Kanal „wan" (internal/wan)
// und die reinen Rechnungen darauf. Seit 25.10.2026 gibt es zwei Router:
// openwrt (WOB, forge steht dahinter) und openwrt-zuhause (SON). Oben im
// Summary steht wie bisher der Anschluss hinter forge -- die Probe ist
// sein Maßstab. Unter `routers` steht jeder Router einzeln; für jeden
// außer dem hinter forge gilt, was sein Agent meldet. forge kann dessen
// Internet nicht selbst prüfen (der Weg dorthin läuft über genau dieses
// Internet), darum heißt Stille dort „meldet sich nicht", nie „Internet
// weg".

export interface WanRouter {
  /** Kennung: Host aus dem MQTT-Topic (= SSH-Alias) */
  host: string;
  /** Hostname aus uci */
  name?: string;
  cluster?: string;
  /** Standort, z. B. „WOB", „SON" */
  cluster_name?: string;
  /** probe = forge prüft selbst; agent = es gilt, was der Router meldet */
  quelle: "probe" | "agent";
  /** probe: ok | warning | problem; agent: ok | problem | stumm | unbekannt */
  status: "ok" | "warning" | "problem" | "stumm" | "unbekannt";
  /** Beginn des jetzigen Status */
  seit?: string;
  /** letzte Meldung des Agenten */
  last_seen?: string;
  /** der Agent schweigt (auch beim Router hinter forge) */
  stumm?: boolean;
  wan_up?: boolean;
  ipv4?: string;
  ipv6?: string;
  rx_mbps?: number;
  tx_mbps?: number;
}

export interface WanStatus {
  ok: boolean;
  status: "ok" | "warning" | "problem";
  consec_fails: number;
  last_check: string;
  last_success: string;
  last_failure?: string;
  last_failure_msg?: string;
  target: string;
  /** Beginn des jetzigen Probe-Status (Anfang eines Ausfalls) */
  seit?: string;
  // IPv4/IPv6 + WAN-Bandbreite des Routers hinter forge (sein Agent).
  ipv4?: string;
  ipv6?: string;
  rx_mbps?: number;
  tx_mbps?: number;
  /** Kennung des Routers, der die Probe trägt */
  router?: string;
  /** jeder Router, von dem ein Frame da ist -- der hinter forge zuerst */
  routers?: WanRouter[];
}

/** Kennung eines Agent-Snapshots: der Router aus dem Topic. Ältere
 *  Rahmen (vor 25.10.2026) tragen ihn noch nicht -- dann aus dem Topic
 *  gelesen, zuletzt der uci-Name. */
export function routerKennung(a: Pick<OpenwrtSnapshot, "host" | "topic"> & { router?: string }): string {
  if (a.router) return a.router;
  const m = /^homelab\/openwrt\/([^/]+)\/wan$/.exec(a.topic ?? "");
  if (m) return m[1].toLowerCase();
  return (a.host ?? "").toLowerCase();
}

/** Wie ein Router heißt: der Standort, sonst sein Name, sonst die Kennung. */
export function routerName(r: Pick<WanRouter, "host" | "name" | "cluster_name">): string {
  return r.cluster_name || r.name || r.host;
}

/** Die Router außer dem hinter forge, in der Reihenfolge des Servers. */
export function weitereRouter(wan: WanStatus | undefined): WanRouter[] {
  return (wan?.routers ?? []).filter((r) => r.quelle === "agent");
}

/** Der Eintrag des Routers hinter forge, falls er sich gemeldet hat. */
export function probeRouter(wan: WanStatus | undefined): WanRouter | undefined {
  if (!wan?.routers) return undefined;
  return wan.routers.find((r) => r.quelle === "probe");
}

/** Der Agent-Snapshot zu einer Kennung. */
export function agentFuer(agents: OpenwrtSnapshot[], kennung: string | undefined): OpenwrtSnapshot | undefined {
  if (!kennung) return undefined;
  return agents.find((a) => routerKennung(a) === kennung);
}

/** Der Agent des Routers hinter forge. Ohne Kennung im Summary (ältere
 *  Antwort) wie bis 25.10.2026: der erste. */
export function probeAgent(wan: WanStatus | undefined, agents: OpenwrtSnapshot[]): OpenwrtSnapshot | undefined {
  if (!wan?.router) return agents[0];
  return agentFuer(agents, wan.router) ?? (agents.length === 1 && weitereRouter(wan).length === 0 ? agents[0] : undefined);
}

export type WanTon = "" | "ok" | "warn" | "danger";

/** Zustand eines Routers, dessen Internet forge nicht selbst prüft, in
 *  einem Wort. */
export function agentWort(r: WanRouter): { text: string; ton: WanTon } {
  switch (r.status) {
    case "ok":
      return { text: "meldet sich", ton: "ok" };
    case "problem":
      return { text: "WAN unten", ton: "danger" };
    case "stumm":
      return { text: "meldet sich nicht", ton: "warn" };
    default:
      // Das Internet hinter forge ist selbst weg -- kein Urteil.
      return { text: "unbekannt", ton: "" };
  }
}

/** Ist alles am Internet in Ordnung -- hinter forge und an jedem anderen
 *  Router? „unbekannt" gibt es nur, wenn forge selbst ohne Internet ist. */
export function internetAllesOk(wan: WanStatus | undefined): boolean {
  if (wan?.status !== "ok") return false;
  return weitereRouter(wan).every((r) => r.status === "ok");
}

/** Was am Internet gerade nicht stimmt, je Router ein Stück -- für den
 *  Gesamtsatz des Systemstatus. Ein Router allein: „Internet weg" wie
 *  bisher. */
export function internetBefunde(wan: WanStatus | undefined): string[] {
  const out: string[] = [];
  const mehrere = weitereRouter(wan).length > 0;
  const hier = probeRouter(wan);
  if (wan?.ok === false) out.push(mehrere && hier ? `Internet ${routerName(hier)} weg` : "Internet weg");
  for (const r of weitereRouter(wan)) {
    if (r.status === "problem") out.push(`Internet ${routerName(r)} weg`);
    else if (r.status === "stumm") out.push(`${routerName(r)} meldet sich nicht`);
  }
  return out;
}

/** Die synthetischen Meldungen zum Internet, je Router eine. Die des
 *  Routers hinter forge heißt weiter „wan" (gelesene Meldungen bleiben
 *  gelesen); jeder andere „wan:<kennung>".
 *
 *  `ts` ist der Anfang der Störung (`seit`), nicht der letzte Fehlschlag:
 *  der Fingerabdruck eines „gelesen" hängt daran (alertFingerprint), und
 *  mit dem letzten Fehlschlag galt ein „gelesen" nur bis zum nächsten
 *  Probe, 30 s später. Kein `eventId`: die Meldung kommt aus dem
 *  Live-Zustand; das Ereignis dahinter (wan_outage, wan_stumm) steht im
 *  Verlauf (DOPPEL_TYPE_EXCLUDE). */
export function wanMeldungen(wan: WanStatus | undefined): Alert[] {
  const out: Alert[] = [];
  if (!wan) return out;
  const weitere = weitereRouter(wan);
  const hier = probeRouter(wan);
  if (wan.status === "problem") {
    out.push({
      id: "wan",
      severity: "problem",
      title: weitere.length > 0 && hier ? `Internet ${routerName(hier)} · keine Verbindung` : "Internet · keine Verbindung",
      detail: `${wan.consec_fails} Probes fehlgeschlagen · Ziel ${wan.target}`,
      ts: wan.seit ?? wan.last_failure ?? undefined,
      href: "/infra/network",
    });
  }
  for (const r of weitere) {
    const name = routerName(r);
    if (r.status === "problem") {
      out.push({
        id: `wan:${r.host}`,
        severity: "problem",
        title: `Internet ${name} · weg`,
        detail: "Der Router meldet seine WAN-Schnittstelle unten",
        ts: r.seit,
        href: "/infra/network",
      });
    } else if (r.status === "stumm") {
      out.push({
        id: `wan:${r.host}`,
        severity: "warning",
        title: `Router ${name} · meldet sich nicht`,
        detail: "Ob dort das Internet weg ist oder nur der Weg dahin (Tailscale), lässt sich von forge aus nicht sagen",
        ts: r.seit,
        href: "/infra/network",
      });
    }
  }
  return out;
}

// ── Internet ────────────────────────────────────────────────────────

export interface Leitung {
  id: "ipv4" | "ipv6" | "tailscale";
  name: string;
  unter: string;
  zustand: NetzZustand;
  adresse: string;
  rx: number | null;
  tx: number | null;
  /** Sekunden seit dem letzten Aufbau, sonst null */
  seit: number | null;
}

function tunnel(agent: OpenwrtSnapshot | undefined, name: string) {
  return agent?.tunnels?.find((t) => t.name === name);
}

/** Die Leitungen ins Internet: IPv4 und IPv6 über den Router, dazu
 *  Tailscale als Fernzugang. IPv4 trägt die Probe (1.1.1.1:443), weil sie
 *  prüft, ob das Internet wirklich antwortet. */
export function leitungen(wan: WanStatus | undefined, agent: OpenwrtSnapshot | undefined): Leitung[] {
  const out: Leitung[] = [];
  const v4 = tunnel(agent, "wan");
  let z4: NetzZustand;
  if (wan?.status === "problem") z4 = { text: "unten", ton: "danger" };
  else if (wan?.status === "warning") z4 = { text: "wackelig", ton: "warn" };
  else if (v4 && !v4.up) z4 = { text: "unten", ton: "danger" };
  else if (wan?.status === "ok" || v4?.up) z4 = { text: "oben", ton: "ok" };
  else z4 = { text: "unbekannt", ton: "" };
  out.push({
    id: "ipv4",
    name: "IPv4",
    unter: "über den Router",
    zustand: z4,
    adresse: v4?.ipv4 ?? wan?.ipv4 ?? "",
    rx: agent ? agent.rx_mbps : (wan?.rx_mbps ?? null),
    tx: agent ? agent.tx_mbps : (wan?.tx_mbps ?? null),
    seit: v4?.up ? v4.uptime : null,
  });
  const v6 = tunnel(agent, "wan6");
  if (v6 || wan?.ipv6) {
    out.push({
      id: "ipv6",
      name: "IPv6",
      unter: "über den Router",
      // IPv6 weg, IPv4 da: das Netz geht, aber nicht alles -- gelb.
      zustand: v6 && !v6.up ? { text: "unten", ton: "warn" } : { text: "oben", ton: "ok" },
      adresse: v6?.ipv6 ?? wan?.ipv6 ?? "",
      rx: null,
      tx: null,
      seit: v6?.up ? v6.uptime : null,
    });
  }
  const ts = agent?.tailscale;
  if (ts) {
    out.push({
      id: "tailscale",
      name: "Tailscale",
      unter: "Fernzugang",
      zustand: ts.online ? { text: "läuft", ton: "ok" } : { text: "aus", ton: "warn" },
      adresse: ts.ipv4 ?? "",
      rx: ts.rx_mbps ?? null,
      tx: ts.tx_mbps ?? null,
      seit: null,
    });
  }
  return out;
}

/** Lage der Internet-Sektion: die Ausnahme vorn, sonst „erreichbar" mit
 *  der Bandbreite. `stumm`: der Agent des Routers schweigt (sein letzter
 *  Frame ist alt) -- dasselbe wie gar keiner. */
export function internetLage(wan: WanStatus | undefined, agent: OpenwrtSnapshot | undefined, zeilen: Leitung[], stumm = false): LageTeil[] {
  if (!wan && !agent) return [];
  const teile: LageTeil[] = [];
  for (const l of zeilen) {
    if (l.zustand.ton === "danger" || l.zustand.ton === "warn") teile.push({ text: `${l.name} ${l.zustand.text}`, ton: l.zustand.ton });
  }
  if (!agent || stumm) teile.push({ text: "Router meldet sich nicht", ton: "warn" });
  const v4 = zeilen.find((l) => l.id === "ipv4");
  if (v4 && (!v4.zustand.ton || v4.zustand.ton === "ok")) {
    teile.push({ text: "erreichbar", ton: "ok" });
    if (v4.rx != null && v4.tx != null) teile.push({ text: `↓ ${mbit(v4.rx)} / ↑ ${mbit(v4.tx)} Mbit/s` });
  }
  return teile;
}

// ── Mehrere Router (seit 25.10.2026) ───────────────────────────────
//
// Je Router ein Block, der hinter forge zuerst. Ein Router allein sieht
// aus wie bis dahin: ein Block „Internet". Für jeden anderen Router gilt,
// was sein Agent meldet -- die Zeilen sagen es („laut Router"), und
// schweigt er, heißt es „meldet sich nicht", nie „unten".

/** Die Leitungen eines Routers, dessen Internet forge nicht selbst prüft. */
export function agentLeitungen(r: WanRouter, agent: OpenwrtSnapshot | undefined): Leitung[] {
  const out: Leitung[] = [];
  const frisch = r.status === "ok" || r.status === "problem";
  const wort = agentWort(r);
  const still: NetzZustand = { text: wort.text, ton: wort.ton };
  const v4 = frisch ? tunnel(agent, "wan") : undefined;
  out.push({
    id: "ipv4",
    name: "IPv4",
    unter: "laut Router",
    zustand: r.status === "ok" ? { text: "oben", ton: "ok" } : r.status === "problem" ? { text: "unten", ton: "danger" } : still,
    adresse: v4?.ipv4 ?? r.ipv4 ?? "",
    // Alte Werte eines stillen Routers wären erfunden: „—".
    rx: frisch ? (agent?.rx_mbps ?? r.rx_mbps ?? null) : null,
    tx: frisch ? (agent?.tx_mbps ?? r.tx_mbps ?? null) : null,
    seit: v4?.up ? v4.uptime : null,
  });
  const v6 = tunnel(agent, "wan6");
  if (v6 || r.ipv6) {
    out.push({
      id: "ipv6",
      name: "IPv6",
      unter: "laut Router",
      zustand: !frisch ? still : v6 && !v6.up ? { text: "unten", ton: "warn" } : { text: "oben", ton: "ok" },
      adresse: v6?.ipv6 ?? r.ipv6 ?? "",
      rx: null,
      tx: null,
      seit: frisch && v6?.up ? v6.uptime : null,
    });
  }
  const ts = agent?.tailscale;
  if (ts) {
    out.push({
      id: "tailscale",
      name: "Tailscale",
      unter: "Fernzugang",
      zustand: !frisch ? still : ts.online ? { text: "läuft", ton: "ok" } : { text: "aus", ton: "warn" },
      adresse: ts.ipv4 ?? "",
      rx: frisch ? (ts.rx_mbps ?? null) : null,
      tx: frisch ? (ts.tx_mbps ?? null) : null,
      seit: null,
    });
  }
  return out;
}

/** Lage eines Routers, dessen Internet sein Agent meldet. */
export function agentLage(r: WanRouter, zeilen: Leitung[]): LageTeil[] {
  switch (r.status) {
    case "problem":
      return [{ text: "WAN unten laut Router", ton: "danger" }];
    case "stumm":
      return [{ text: "Router meldet sich nicht", ton: "warn" }];
    case "unbekannt":
      return [{ text: "unbekannt, solange das Internet hier weg ist" }];
  }
  const teile: LageTeil[] = [];
  for (const l of zeilen) {
    if (l.zustand.ton === "danger" || l.zustand.ton === "warn") teile.push({ text: `${l.name} ${l.zustand.text}`, ton: l.zustand.ton });
  }
  teile.push({ text: "meldet sich", ton: "ok" });
  const v4 = zeilen.find((l) => l.id === "ipv4");
  if (v4 && v4.rx != null && v4.tx != null) teile.push({ text: `↓ ${mbit(v4.rx)} / ↑ ${mbit(v4.tx)} Mbit/s` });
  return teile;
}

/** Ein Block der Internet-Sektion: ein Router mit seinen Leitungen. */
export interface Anschluss {
  /** Kennung des Routers ("" solange hinter forge keiner gemeldet hat) */
  key: string;
  /** Standort oder Name -- nur gezeigt, wenn es mehrere gibt */
  name: string;
  /** mehr als ein Router: der Name gehört in den Titel */
  mehrere: boolean;
  quelle: "probe" | "agent";
  router?: WanRouter;
  agent?: OpenwrtSnapshot;
  zeilen: Leitung[];
  lage: LageTeil[];
}

/** Die Blöcke der Internet-Sektion: der Router hinter forge zuerst
 *  (Probe), dann jeder andere (Agent), in der Reihenfolge des Servers. */
export function anschluesse(wan: WanStatus | undefined, agents: OpenwrtSnapshot[]): Anschluss[] {
  const weitere = weitereRouter(wan);
  const mehrere = weitere.length > 0;
  const hier = probeRouter(wan);
  const agent = probeAgent(wan, agents);
  const zeilen = leitungen(wan, agent);
  const out: Anschluss[] = [
    {
      key: wan?.router ?? hier?.host ?? "",
      name: hier ? routerName(hier) : (wan?.router ?? ""),
      mehrere,
      quelle: "probe",
      router: hier,
      agent,
      zeilen,
      lage: internetLage(wan, agent, zeilen, hier?.stumm === true),
    },
  ];
  for (const r of weitere) {
    const a = agentFuer(agents, r.host);
    const z = agentLeitungen(r, a);
    out.push({ key: r.host, name: routerName(r), mehrere, quelle: "agent", router: r, agent: a, zeilen: z, lage: agentLage(r, z) });
  }
  return out;
}

/** Die zweite Zeile der Internet-Karte für einen weiteren Router:
 *  Bandbreite, solange er sich meldet, sonst sein Zustand in Worten. */
export function routerKurz(r: WanRouter): { name: string; text: string; ton: "" | "ok" | "warn" | "danger" } {
  const name = routerName(r);
  if (r.status === "ok") return { name, text: `↓ ${mbit(r.rx_mbps ?? 0)} / ↑ ${mbit(r.tx_mbps ?? 0)}`, ton: "" };
  const w = agentWort(r);
  return { name, text: w.text, ton: w.ton };
}

// ── Im Netz ─────────────────────────────────────────────────────────

const ART_RECHNER: Record<string, string> = { server: "Server", router: "Router", dns: "DNS", iot: "IoT" };
const ART_GERAET: Record<string, string> = {
  phone: "Handy", iot: "IoT", device: "Gerät", switch: "Switch", camera: "Kamera", router: "Router", compute: "Rechner",
};
/** Geräte, die das Haus verlassen: offline heißt „unterwegs", leise. */
const UNTERWEGS = new Set(["phone"]);

export interface ImNetzZeile {
  key: string;
  name: string;
  ip: string;
  macs: string[];
  /** Kategorien für das Schild, z. B. ["Rechner", "Router"] */
  arten: string[];
  zustand: NetzZustand;
  /** gesetzt, wenn ein Rechner dabei ist: seine ID im Cluster */
  rechnerId?: string;
  /** Rechner und Gerät zusammengeführt */
  zusammengefuehrt: boolean;
}

/** Wie auf der Cluster-Seite (nochNichtInBetrieb in lib/cluster/rechner.ts). */
const NOCH_NICHT = "noch nicht in Betrieb";

/** Rechner und Geräte eines Standorts als eine Liste. Einträge mit
 *  derselben MAC oder IP werden eine Zeile (MacBook, iMac, AP1 standen
 *  bis 30.09. doppelt da); der Rechner-Zustand gewinnt. */
export function imNetz(site: Pick<NetworkTopology, "hosts" | "devices">): ImNetzZeile[] {
  const zeilen: ImNetzZeile[] = [];
  for (const h of site.hosts ?? []) {
    const schlaeft = h.sleep_ok && h.online !== true;
    zeilen.push({
      key: "host:" + h.id,
      name: h.name,
      ip: h.ip,
      macs: h.mac ? [h.mac.toLowerCase()] : [],
      arten: ["Rechner", ...(ART_RECHNER[h.role] ? [ART_RECHNER[h.role]] : [])],
      zustand: schlaeft
        ? { text: "schläft", ton: "" }
        : h.noch_nicht_in_betrieb
          ? { text: NOCH_NICHT, ton: "" }
          : h.online === true
          ? { text: "da", ton: "ok" }
          : h.online === false
            ? { text: "offline", ton: "danger" }
            : { text: "unbekannt", ton: "" },
      rechnerId: h.id,
      zusammengefuehrt: false,
    });
  }
  for (const d of site.devices ?? []) {
    const mac = d.mac ? d.mac.toLowerCase() : "";
    const treffer = zeilen.find((z) => (mac && z.macs.includes(mac)) || (d.ip && z.ip === d.ip));
    const art = ART_GERAET[d.role] ?? (d.role ? d.role : "Gerät");
    if (treffer) {
      if (mac && !treffer.macs.includes(mac)) treffer.macs.push(mac);
      if (!treffer.arten.includes(art)) treffer.arten.push(art);
      treffer.zusammengefuehrt = true;
      continue;
    }
    zeilen.push({
      key: "geraet:" + (mac || d.ip || d.name),
      name: d.name,
      ip: d.ip,
      macs: mac ? [mac] : [],
      arten: [art],
      zustand:
        d.online === true
          ? { text: "da", ton: "ok" }
          : d.online === false
            ? UNTERWEGS.has(d.role)
              ? { text: "unterwegs", ton: "" }
              : { text: "antwortet nicht", ton: "warn" }
            : { text: "unbekannt", ton: "" },
      zusammengefuehrt: false,
    });
  }
  return zeilen;
}

function liste(namen: string[]): string {
  if (namen.length <= 2) return namen.join(" und ");
  return `${namen.slice(0, 2).join(", ")} und ${namen.length - 2} weitere`;
}

/** Lage je Standort: wer fehlt, vorn und farbig; dann „alle da" oder
 *  „sonst alle da"; dann das leise Normale (unterwegs, schläft). */
export function standortLage(zeilen: ImNetzZeile[]): LageTeil[] {
  const teile: LageTeil[] = [];
  const nach = (text: string) => zeilen.filter((z) => z.zustand.text === text).map((z) => z.name);
  const offline = nach("offline");
  if (offline.length) teile.push({ text: `offline: ${liste(offline)}`, ton: "danger" });
  const stumm = nach("antwortet nicht");
  if (stumm.length) teile.push({ text: `${liste(stumm)} ${stumm.length === 1 ? "antwortet" : "antworten"} nicht`, ton: "warn" });
  const da = zeilen.filter((z) => z.zustand.text === "da").length;
  if (zeilen.length === 0) return [];
  if (da > 0) teile.push({ text: offline.length || stumm.length ? "sonst alle da" : "alle da", ton: "ok" });
  const unterwegs = nach("unterwegs");
  if (unterwegs.length) teile.push({ text: `${liste(unterwegs)} unterwegs` });
  const schlafen = nach("schläft");
  if (schlafen.length) teile.push({ text: `${liste(schlafen)} ${schlafen.length === 1 ? "schläft" : "schlafen"}` });
  const neu = nach(NOCH_NICHT);
  if (neu.length) teile.push({ text: `${liste(neu)} ${NOCH_NICHT}` });
  return teile;
}

/** Laufzeit „14 T 17 h" / „5 h 12 min". */
export function seitText(s: number | null): string {
  if (s == null) return "—";
  const t = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  if (t > 0) return `${t} T ${h} h`;
  return `${h} h ${Math.floor((s % 3600) / 60)} min`;
}

// ── Interfaces: lesbare Bezeichnungen ───────────────────────────────
// Register „Interfaces" (02.10.2026, Tim: „wirksam machen"): eine
// Bezeichnung gilt für den Interface-Namen auf jedem Rechner (eth0 auf
// network und zigbee ist beide Male das LAN). Wo ein Interface steht,
// steht die Bezeichnung; der Systemname wandert in den Tooltip.

export interface NetIf {
  id: string;
  label: string;
  position: number;
}

/** Bezeichnung zum Interface-Namen, sonst der Name selbst. */
export function ifaceName(id: string, bezeichnungen: NetIf[] | undefined): string {
  const b = bezeichnungen?.find((n) => n.id === id)?.label.trim();
  return b || id;
}

export interface IfaceZeile {
  id: string;
  label: string;
  /** Wo das Interface vorkommt: Rechner (aus link_state) und Router. */
  wo: string[];
}

/** Zeilen des Registers: jedes bekannte Interface (Rechner + Router)
 *  und jede gespeicherte Bezeichnung, auch wenn das Interface gerade
 *  nirgends gemeldet wird. */
export function interfaceZeilen(bezeichnungen: NetIf[], gesehen: { iface: string; wo: string }[]): IfaceZeile[] {
  const zeilen = new Map<string, IfaceZeile>();
  for (const b of bezeichnungen) zeilen.set(b.id, { id: b.id, label: b.label, wo: [] });
  for (const g of gesehen) {
    if (!g.iface) continue;
    const z = zeilen.get(g.iface) ?? { id: g.iface, label: "", wo: [] };
    if (g.wo && !z.wo.includes(g.wo)) z.wo.push(g.wo);
    zeilen.set(g.iface, z);
  }
  for (const z of zeilen.values()) z.wo.sort((a, b) => a.localeCompare(b, "de"));
  return [...zeilen.values()].sort((a, b) => a.id.localeCompare(b.id, "de", { numeric: true }));
}

/** Lage: wie viele benannt sind. Ein Interface ohne Bezeichnung ist kein
 *  Handgriff -- leise, ohne Ton. */
export function interfaceLage(zeilen: IfaceZeile[]): LageTeil[] {
  const benannt = zeilen.filter((z) => z.label.trim()).length;
  const ohne = zeilen.length - benannt;
  const teile: LageTeil[] = [];
  if (benannt) teile.push({ text: `${benannt} benannt` });
  if (ohne) teile.push({ text: `${ohne} ohne Bezeichnung` });
  return teile;
}
